import { FieldIconButton } from "@patient-watch/ui";
import { Input } from "@patient-watch/ui/input";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useState } from "react";

import {
    type AddOwnOptionProps,
    OWN_OPTION_MAX_LENGTH,
    OWN_OPTIONS_PER_QUESTION,
    type OwnOptionCopy,
    OwnOptionInUseError,
} from "./OwnOptionControls";

/** "Add your own option" as a Select or ComboBox takes it. */
export type FieldOwnOptions = Omit<AddOwnOptionProps, "inputId" | "onAdded" | "multiple">;

/** The value of the "Other" item in a dropdown list. Never saved: choosing it opens the text box. */
export const ADD_OWN_OPTION_VALUE = "__add_own_option__";

type Editing = { kind: "add"; initial: string } | { kind: "rename"; value: string; initial: string } | null;

function problemWith(text: string, copy: OwnOptionCopy): string | null {
    const trimmed = text.trim();
    if (trimmed.length === 0) return copy.empty;
    if (trimmed.length > OWN_OPTION_MAX_LENGTH) return copy.tooLong;
    return null;
}

/** A text box the same height as the field it replaces, with confirm and cancel at its end. */
function InlineOwnOptionInput({
    testId,
    initial,
    confirmText,
    copy,
    onConfirm,
    onCancel,
}: {
    testId: string;
    initial: string;
    confirmText: string;
    copy: OwnOptionCopy;
    onConfirm: (label: string) => Promise<void>;
    onCancel: () => void;
}) {
    const [text, setText] = useState(initial);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const confirm = async () => {
        const problem = problemWith(text, copy);
        if (problem) {
            setError(problem);
            return;
        }
        setSaving(true);
        try {
            await onConfirm(text.trim());
        } catch {
            setError(copy.failed);
        } finally {
            setSaving(false);
        }
    };

    // Enter would submit the whole form page; here it confirms the box instead.
    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            void confirm();
        } else if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
        }
    };

    return (
        <div className="space-y-1.5" data-testid={testId}>
            <div className="relative">
                <Input
                    autoFocus
                    value={text}
                    maxLength={OWN_OPTION_MAX_LENGTH}
                    placeholder={copy.placeholder}
                    aria-invalid={error ? true : undefined}
                    className="pr-20"
                    data-testid={`${testId}-text`}
                    onChange={(event) => {
                        setText(event.target.value);
                        setError(null);
                    }}
                    onKeyDown={onKeyDown}
                />
                <div className="top-1/2 right-1 absolute flex items-center gap-0.5 -translate-y-1/2">
                    <FieldIconButton
                        aria-label={confirmText}
                        disabled={saving}
                        data-testid={`${testId}-confirm`}
                        onClick={() => void confirm()}
                    >
                        <Check aria-hidden />
                    </FieldIconButton>
                    <FieldIconButton
                        aria-label={copy.cancel}
                        disabled={saving}
                        data-testid={`${testId}-cancel`}
                        onClick={onCancel}
                    >
                        <X aria-hidden />
                    </FieldIconButton>
                </div>
            </div>
            {error ? (
                <p className="text-destructive text-sm" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
}

/**
 * The state a dropdown needs for "add your own option": the "Other" item in its list, a text box that takes
 * the trigger's place while adding or renaming, and edit and delete beside the trigger when the answer is one
 * of the patient's own options.
 */
export function useFieldOwnOptions({
    ownOptions: config,
    name,
    value,
    onChange,
}: {
    ownOptions: FieldOwnOptions | undefined;
    name: string;
    value: unknown;
    onChange: (value: string | null) => void;
}) {
    const [editing, setEditing] = useState<Editing>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!config) return null;
    const { copy } = config;
    const selected = value == null || value === "" ? null : String(value);
    const own = selected ? config.ownOptions.find((option) => option.value === selected) : undefined;
    const atLimit = config.ownOptions.length >= OWN_OPTIONS_PER_QUESTION;

    const startAdd = (initial = "") => {
        setError(null);
        setEditing({ kind: "add", initial });
    };

    /** Adds straight away, from text typed elsewhere (a combobox's search box). */
    const addNow = async (text: string) => {
        const problem = problemWith(text, copy);
        if (problem) {
            startAdd(text);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            onChange(await config.onAdd(text.trim()));
        } catch {
            setError(copy.failed);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (optionValue: string) => {
        setBusy(true);
        setError(null);
        try {
            await config.onDelete(optionValue);
            if (selected === optionValue) onChange(null);
        } catch (failure) {
            setError(failure instanceof OwnOptionInUseError ? copy.inUse : copy.failed);
        } finally {
            setBusy(false);
        }
    };

    const editor: ReactNode = editing ? (
        <InlineOwnOptionInput
            key={editing.kind === "add" ? "add" : editing.value}
            testId={
                editing.kind === "add" ? `form-add-option-${name}-editor` : `form-own-option-rename-${editing.value}`
            }
            initial={editing.initial}
            confirmText={editing.kind === "add" ? copy.add : copy.save}
            copy={copy}
            onConfirm={async (text) => {
                if (editing.kind === "add") onChange(await config.onAdd(text));
                else if (text !== editing.initial) await config.onRename(editing.value, text);
                setEditing(null);
            }}
            onCancel={() => setEditing(null)}
        />
    ) : null;

    const actions: ReactNode = own ? (
        <div className="flex items-center gap-0.5 shrink-0">
            <FieldIconButton
                aria-label={copy.edit(own.label)}
                disabled={busy}
                className="size-9"
                data-testid={`form-own-option-edit-${own.value}`}
                onClick={() => {
                    setError(null);
                    setEditing({ kind: "rename", value: own.value, initial: own.label });
                }}
            >
                <Pencil aria-hidden />
            </FieldIconButton>
            <FieldIconButton
                aria-label={copy.delete(own.label)}
                disabled={busy}
                className="size-9 hover:text-destructive"
                data-testid={`form-own-option-delete-${own.value}`}
                onClick={() => void remove(own.value)}
            >
                <Trash2 aria-hidden />
            </FieldIconButton>
        </div>
    ) : null;

    const message: ReactNode = error ? (
        <p className="text-destructive text-sm" role="alert">
            {error}
        </p>
    ) : null;

    return {
        /** The "Other" item's text. */
        addLabel: config.label,
        /** "Add", before text typed elsewhere: Add "Gardening". */
        addText: copy.add,
        atLimit,
        limitText: copy.limit,
        isOwn: (optionValue: string) => config.ownOptions.some((option) => option.value === optionValue),
        startAdd,
        addNow,
        /** Shown in place of the trigger while adding or renaming. */
        editor,
        /** Edit and delete, beside the trigger, when the answer is one of the patient's own options. */
        actions,
        message,
        busy,
    };
}

/** Lays out a dropdown trigger with the own-option buttons beside it, or the text box in its place. */
export function FieldWithOwnOptions({
    own,
    children,
}: {
    own: ReturnType<typeof useFieldOwnOptions>;
    children: ReactNode;
}) {
    if (!own) return <>{children}</>;
    return (
        <div className="space-y-1.5 w-full">
            {own.editor ?? (
                <div className="flex items-center gap-1 w-full">
                    <div className="flex-1 min-w-0">{children}</div>
                    {own.actions}
                </div>
            )}
            {own.message}
        </div>
    );
}
